import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchLowStockMedicines } from "../services/pharmacyLowStockService";
import {
  canShowPharmacyLowStockAlert,
  hasShownLowStockAlertThisSession,
  markLowStockAlertShown,
  readLowStockQuantityThreshold,
} from "../utils/medicineLowStock";
import { getOrganizationId } from "../utils/organizationUtils";
import { isLoginValidationInProgress } from "../utils/loginSession";
import PharmacyLowStockAlertDialog from "./PharmacyLowStockAlertDialog";

const PharmacyLowStockAlertHandler = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [medicines, setMedicines] = useState([]);
  const fetchStartedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || isLoginValidationInProgress()) return;
    if (!canShowPharmacyLowStockAlert(user?.roleName)) return;
    if (hasShownLowStockAlertThisSession(user?.id)) return;
    if (fetchStartedRef.current) return;

    fetchStartedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const organizationId = getOrganizationId(user);
        const list = await fetchLowStockMedicines(organizationId);
        if (!cancelled && list.length > 0) {
          setMedicines(list);
          setOpen(true);
        }
      } catch (error) {
        console.warn("[PharmacyLowStockAlert] Unable to load low stock list:", error);
      } finally {
        if (!cancelled) {
          markLowStockAlertShown(user?.id);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, user?.roleName]);

  const handleClose = () => setOpen(false);

  const handleGoToInventory = () => {
    setOpen(false);
    navigate("/inventory");
  };

  if (!isAuthenticated || !canShowPharmacyLowStockAlert(user?.roleName)) {
    return null;
  }

  return (
    <PharmacyLowStockAlertDialog
      open={open}
      onClose={handleClose}
      medicines={medicines}
      quantityThreshold={readLowStockQuantityThreshold(getOrganizationId(user))}
      onGoToInventory={handleGoToInventory}
    />
  );
};

export default PharmacyLowStockAlertHandler;
